import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { RevealTitle, FadeIn, MagneticButton } from "./Reveal";

const CAMPAIGN_TYPES = ["TikTok Video", "Reels", "TV Reklamı", "Elçilik", "Etkinlik", "Müzik", "Diğer"];
const BUDGETS = ["50.000 TL altı", "50.000 – 150.000 TL", "150.000 – 500.000 TL", "500.000 TL – 1M TL", "1M TL üzeri", "Belirtmek istemiyorum"];

const SuccessOverlay = ({ onClose }) => (
  <motion.div
    data-testid="contact-success"
    className="absolute inset-0 z-20 bg-[#0A0A0A]/95 flex flex-col items-center justify-center text-center p-8"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
  >
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
      <motion.path
        d="M 20 60 C 20 35, 45 25, 60 40 C 75 55, 50 75, 38 65 C 26 55, 45 42, 58 55 L 52 68 L 80 40"
        stroke="#3E5C3A"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, ease: "easeInOut" }}
      />
      <motion.path
        d="M 38 62 L 55 80 L 95 38"
        stroke="#C1121F"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 1.2, duration: 0.6, ease: "easeOut" }}
      />
    </svg>
    <h3 className="font-blackletter text-3xl md:text-4xl text-[#EDEDEA] mt-6">Aileye Hoş Geldiniz</h3>
    <p className="mt-3 text-[#A3A3A3] text-sm max-w-sm">Başvurunuz alındı. En kısa sürede size dönüş yapılacak.</p>
    <button data-testid="contact-success-close" onClick={onClose} className="mt-8 text-xs tracking-[0.3em] text-[#8B0000] uppercase border border-[#8B0000]/50 px-6 py-3 hover:bg-[#8B0000]/10 transition-colors">
      Yeni Başvuru
    </button>
  </motion.div>
);

const EMPTY = { company: "", name: "", email: "", phone: "", campaign_type: "", budget: "", target_date: "", message: "", kvkk: false, website: "" };

export const ContactSection = ({ texts, settings }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Firma adı zorunludur";
    if (!form.name.trim()) e.name = "Ad soyad zorunludur";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Geçerli bir e-posta adresi girin";
    if (!form.message.trim()) e.message = "Mesaj zorunludur";
    if (!form.kvkk) e.kvkk = "KVKK onayı zorunludur";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setApiError("");
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm(EMPTY);
    } catch (err) {
      setApiError(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const Err = ({ k }) => (errors[k] ? <p data-testid={`error-${k}`} className="text-[#C1121F] text-xs mt-1.5">{errors[k]}</p> : null);

  return (
    <section id="iletisim" data-testid="contact-section" className="relative z-10 py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0D0D0D]">
      <p className="text-[#525252] tracking-[0.5em] text-xs uppercase mb-3">— İletişim</p>
      <RevealTitle testId="contact-title" className="font-blackletter text-4xl sm:text-5xl lg:text-6xl text-[#EDEDEA] mb-16">
        {texts.contact_title || "Ailenin Bir Parçası Olun"}
      </RevealTitle>
      <div className="grid lg:grid-cols-3 gap-12">
        <FadeIn className="lg:col-span-2 relative border border-[#232323] bg-[#101010] p-6 md:p-10">
          <AnimatePresence>{success && <SuccessOverlay onClose={() => setSuccess(false)} />}</AnimatePresence>
          <form onSubmit={submit} noValidate>
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <input data-testid="contact-company" className="field-dark" placeholder="Firma Adı *" value={form.company} onChange={(e) => set("company", e.target.value)} />
                <Err k="company" />
              </div>
              <div>
                <input data-testid="contact-name" className="field-dark" placeholder="Yetkili Ad Soyad *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <Err k="name" />
              </div>
              <div>
                <input data-testid="contact-email" type="email" className="field-dark" placeholder="E-posta *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <Err k="email" />
              </div>
              <input data-testid="contact-phone" className="field-dark" placeholder="Telefon" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <select data-testid="contact-campaign-type" className="field-dark" value={form.campaign_type} onChange={(e) => set("campaign_type", e.target.value)}>
                <option value="">Kampanya Türü Seçin</option>
                {CAMPAIGN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="contact-budget" className="field-dark" value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                <option value="">Tahmini Bütçe Aralığı</option>
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <div className="md:col-span-2">
                <label className="text-xs text-[#525252] tracking-wider uppercase block mb-1.5">Hedef Tarih</label>
                <input data-testid="contact-date" type="date" className="field-dark" value={form.target_date} onChange={(e) => set("target_date", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <textarea data-testid="contact-message" className="field-dark min-h-[140px] resize-y" placeholder="Mesajınız — kampanyanızı anlatın *" value={form.message} onChange={(e) => set("message", e.target.value)} />
                <Err k="message" />
              </div>
            </div>
            <label className="flex items-start gap-3 mt-6 text-xs text-[#A3A3A3] leading-relaxed" data-cursor="ONAYLA">
              <input data-testid="contact-kvkk" type="checkbox" checked={form.kvkk} onChange={(e) => set("kvkk", e.target.checked)} className="mt-0.5 accent-[#8B0000] w-4 h-4" />
              <span>Kişisel verilerimin 6698 sayılı KVKK kapsamında, iş birliği talebimin değerlendirilmesi amacıyla işlenmesini kabul ediyorum. *</span>
            </label>
            <Err k="kvkk" />
            {apiError && <p data-testid="contact-api-error" className="text-[#C1121F] text-sm mt-4">{apiError}</p>}
            <MagneticButton
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              data-cursor="GÖNDER"
              className="mt-8 bg-[#8B0000] hover:bg-[#C1121F] text-[#EDEDEA] px-10 py-4 text-sm tracking-[0.25em] uppercase font-semibold disabled:opacity-50"
            >
              {sending ? "Gönderiliyor..." : "Başvuruyu Gönder"}
            </MagneticButton>
          </form>
        </FadeIn>
        <FadeIn delay={0.2}>
          <h3 className="text-lg font-semibold text-[#EDEDEA]">Doğrudan İletişim</h3>
          <a data-testid="contact-direct-email" href={`mailto:${settings.contact_email}`} className="block mt-3 text-[#C1121F] hover:text-[#EDEDEA] transition-colors">
            {settings.contact_email}
          </a>
          <div className="mt-8 flex flex-col gap-3 text-sm">
            {[["TikTok", settings.tiktok], ["Instagram", settings.instagram], ["YouTube", settings.youtube], ["X", settings.x]].map(([name, url]) => (
              url ? (
                <a key={name} href={url} target="_blank" rel="noreferrer" data-testid={`social-${name.toLowerCase()}`} className="text-[#A3A3A3] hover:text-[#C1121F] transition-colors tracking-wider uppercase text-xs border-b border-[#232323] pb-3 flex justify-between">
                  <span>{name}</span><span>↗</span>
                </a>
              ) : null
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
